import React, { useState, useEffect } from "react";
import RoastLevelSpectrums from "~/components/RoastLevelSpectrums.jsx";
import ContentPDP from "~/components/ContentPDP.jsx";

const COLLECTION_HANDLES = [
  "bright-light",
  "light",
  "medium",
  "full-medium",
  "dark",
  "beyond-dark",
];

const ShopByRoastPDP = () => {
  const [activeCollection, setActiveCollection] = useState("medium");
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const storefrontAccessToken = process.env.REACT_APP_SHOPIFY_STOREFRONT_ACCESSTOKEN;
  const shopifyDomain = process.env.REACT_APP_SHOPIFY_STORE_DOMAIN;

  const activeCollectionHandler = (e) => {
    // console.log("e.currentTarget", e.currentTarget);
    const collectionHandle = e.currentTarget.getAttribute("roastlevel");
    // console.log("🔍 Active Collection:", collectionHandle);

    if (!collectionHandle) return;

    const collection = collections.find((c) => c.collectionHandle === collectionHandle);
    setActiveCollection(collection);
  };

  useEffect(() => {
    async function fetchAllProducts(handle, cursor = null, allProducts = []) {
      // console.log("📌 Fetching collection:", handle, "with cursor:", cursor);

      const query = `
        {
          collectionByHandle(handle: "${handle}") {
            id
            title
            description
            image {
              src
              altText
            }
            products(first: 50, after: ${cursor ? `"${cursor}"` : null}) {
              edges {
                node {
                  id
                  title
                  availableForSale
                  handle
                  onlineStoreUrl
                  priceRange {
                    minVariantPrice {
                      amount
                      currencyCode
                    }
                    maxVariantPrice {
                      amount
                      currencyCode
                    }
                  }
                  variants(first: 50) {
                    edges {
                      node {
                        price {
                          amount
                          currencyCode
                        }
                        title
                      }
                    }
                  }
                  images(first: 1) {
                    edges {
                      node {
                        src
                        altText
                      }
                    }
                  }
                  media(first: 1) {
                    edges {
                      node {
                        ... on Video {
                          sources {
                            url
                            format
                          }
                          previewImage {
                            url
                          }
                        }
                      }
                    }
                  }
                }
              }
              pageInfo {
                hasNextPage
                endCursor
              }
            }
          }
        }
      `;

      try {
        const response = await fetch(`https://${shopifyDomain}/api/2025-01/graphql.json`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token": storefrontAccessToken,
          },
          body: JSON.stringify({ query }),
        });

        const data = await response.json();
        const collection = data?.data?.collectionByHandle;

        if (!collection) {
          console.warn(`⚠️ No data found for collection: ${handle}`);
          return { collectionHandle: handle, products: [] };
        }

        const products = collection?.products?.edges
          .map((edge) => edge.node)
          .filter(
            (product) =>
              typeof product === "object" &&
              product.id &&
              product.title &&
              product.priceRange &&
              typeof product.priceRange.minVariantPrice.amount !== "undefined" &&
              typeof product.priceRange.maxVariantPrice.amount !== "undefined"
          );

        const uniqueProducts = Array.from(
          new Map([...allProducts, ...products].map((p) => [p.id, p])).values()
        );

        // console.log(`✅ Collection: ${handle} - Found ${uniqueProducts.length} products`);

        if (collection?.products?.pageInfo?.hasNextPage) {
          return fetchAllProducts(handle, collection.products.pageInfo.endCursor, uniqueProducts);
        }

        return {
          collectionHandle: handle,
          collectionId: collection?.id,
          collectionDescription: collection?.description,
          collectionImage: collection?.image,
          products: uniqueProducts,
        };
      } catch (error) {
        console.error(`❌ Error fetching collection: ${handle}`, error);
        return {
          collectionHandle: handle,
          collectionId: collection?.id,
          collectionDescription: collection?.description,
          collectionImage: collection?.image,
          products: [],
        };
      }
    }

    async function fetchMultipleCollections() {
      try {
        // console.log("🛠 Fetching collections:", COLLECTION_HANDLES);

        const collectionsData = await Promise.all(
          COLLECTION_HANDLES.map((handle) => fetchAllProducts(handle))
        );

        // console.log("📌 Final Structured Data:", collectionsData);
        setCollections(collectionsData);

        const mediumCollection = collectionsData.find(
          (collection) => collection.collectionHandle === "medium"
        );

        // console.log("🔍 Medium Collection:", mediumCollection);
        setActiveCollection(mediumCollection || collectionsData[0]);
      } catch (err) {
        setError("Failed to fetch collections");
        console.error("❌ Error fetching multiple collections:", err);
      } finally {
        setLoading(false);
      }
    }

    fetchMultipleCollections();
  }, []);

  return (
    <div className="page-container mx-auto p-8 container container--large">
      <span className="text-4xl block text-center font-bold uppercase mb-12 md:mb-16">
        Shop By Roast
      </span>
      {activeCollection && (
        <>
          <RoastLevelSpectrums
            collections={collections}
            activeCollectionHandler={activeCollectionHandler}
            activeCollection={activeCollection}
          />
          <ContentPDP activeCollection={activeCollection} />
        </>
      )}
    </div>
  );
};

export default ShopByRoastPDP;
